function absCosSin(deg: number) {
  const rad = (Number(deg) * Math.PI) / 180;
  return { a: Math.abs(Math.cos(rad)), b: Math.abs(Math.sin(rad)) };
}

/**
 * Forward bbox projection: axis-aligned bbox size for an unrotated rectangle after rotation.
 */
export function getBboxSizeFromUnrotated(
  width: number,
  height: number,
  rotationDeg: number
): { width: number; height: number } {
  const w = Math.max(0.0001, Number(width) || 0.0001);
  const h = Math.max(0.0001, Number(height) || 0.0001);
  const { a, b } = absCosSin(rotationDeg);
  return {
    width: w * a + h * b,
    height: w * b + h * a,
  };
}

/**
 * Recover unrotated (w0,h0) from rotated axis-aligned bbox (W,H).
 * Optional `unrotatedAspectWOverH` disambiguates near 45° (matches web helper).
 */
export function getUnrotatedSizeFromBbox(
  bboxWidth: number,
  bboxHeight: number,
  rotationDeg: number,
  unrotatedAspectWOverH?: number
): { width: number; height: number } {
  const W = Math.max(0.0001, Number(bboxWidth) || 0.0001);
  const H = Math.max(0.0001, Number(bboxHeight) || 0.0001);
  const { a, b } = absCosSin(rotationDeg);
  const denom = a * a - b * b;
  const r =
    typeof unrotatedAspectWOverH === 'number' &&
    Number.isFinite(unrotatedAspectWOverH) &&
    unrotatedAspectWOverH > 0
      ? unrotatedAspectWOverH
      : undefined;
  if (!Number.isFinite(denom) || Math.abs(denom) < 1e-6) {
    if (r) {
      const c = Math.max(0.0001, (a + b) / 2);
      const avg = (W + H) / 2;
      const sum = avg / c;
      const h0 = sum / (1 + r);
      const w0 = r * h0;
      return {
        width: Math.max(0.0001, Number.isFinite(w0) ? w0 : W),
        height: Math.max(0.0001, Number.isFinite(h0) ? h0 : H),
      };
    }
    return { width: W, height: H };
  }
  const w0 = (W * a - H * b) / denom;
  const h0 = (H * a - W * b) / denom;
  return {
    width: Math.max(0.0001, Number.isFinite(w0) ? w0 : W),
    height: Math.max(0.0001, Number.isFinite(h0) ? h0 : H),
  };
}
